import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import posthog from "@/lib/posthog";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HealthCheck } from "@/components/HealthCheck";
import { CookieConsent } from "@/components/CookieConsent";
import { ToastProvider } from "@/contexts/ToastContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { DensityProvider } from "@/contexts/DensityContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Register from "./pages/Register";
import Login from "./pages/Login";
import VerifyEmailPending from "./pages/VerifyEmailPending";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import CreateOrganization from "./pages/onboarding/CreateOrganization";
import ConnectDataSource from "./pages/onboarding/ConnectDataSource";
import SyncingData from "./pages/onboarding/SyncingData";
import Dashboard from "./pages/Dashboard";
import QuickBooksCallback from "./pages/integrations/QuickBooksCallback";
import AuthError from "./pages/AuthError";
import TokenExpired from "./pages/TokenExpired";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LicenseAgreement from "./pages/LicenseAgreement";

function PostHogPageview() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview", { $current_url: window.location.href });
  }, [location.pathname]);
  return null;
}

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary component="App">
        <HealthCheck>
          <Router>
            <PostHogPageview />
            <OrganizationProvider>
            <DensityProvider>
            <ToastProvider>
            <Toaster />
            <Sonner />
            <CookieConsent />
            <Routes>
              {/* Auth routes */}
              <Route path="/" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Onboarding routes */}
              <Route path="/onboarding/organization" element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} />
              <Route path="/onboarding/connect" element={<ProtectedRoute><ConnectDataSource /></ProtectedRoute>} />
              <Route path="/onboarding/syncing" element={<ProtectedRoute><SyncingData /></ProtectedRoute>} />

              {/* Main app */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

              {/* Integration callbacks */}
              <Route path="/integrations/quickbooks/callback" element={<ProtectedRoute><QuickBooksCallback /></ProtectedRoute>} />

              {/* Error / utility routes */}
              <Route path="/auth/error" element={<AuthError />} />
              <Route path="/auth/expired" element={<TokenExpired />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/license-agreement" element={<LicenseAgreement />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            </ToastProvider>
            </DensityProvider>
            </OrganizationProvider>
          </Router>
        </HealthCheck>
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
